import type { FeatureCollection, Geometry } from 'geojson';
import { classifyAlertEvent } from '../config/alerts';
import { alertGeometryStats, alertRings, isPolygonal } from '../lib/geo';
import { fetchJson } from '../lib/fetchJson';
import type { Alert, AlertFeatureProperties } from '../types/alerts';

/**
 * Active flood alerts. Served by the Worker-maintained snapshot through the
 * same-origin Pages Function (/api/alerts). The snapshot resolves zone-based
 * watch footprints server-side, so every feature has a polygon.
 */
export async function fetchAlerts(signal?: AbortSignal): Promise<Alert[]> {
  const fc = await fetchJson<FeatureCollection<Geometry, AlertFeatureProperties>>('/api/alerts', {
    signal,
    timeoutMs: 20_000,
  });
  return alertsFromFeatureCollection(fc);
}

export function alertsFromFeatureCollection(fc: FeatureCollection<Geometry, AlertFeatureProperties>): Alert[] {
  return (fc.features ?? []).map((f, index) => {
    const p = f.properties ?? ({ event: 'Unknown' } as AlertFeatureProperties);
    const geometry = isPolygonal(f.geometry) ? f.geometry : null;
    const rings = alertRings(geometry);
    const stats = alertGeometryStats(rings);
    const event = p.event ?? 'Unknown';
    return {
      id: String(f.id ?? `alert-${index}`),
      event,
      kind: classifyAlertEvent(event),
      headline: p.headline ?? event,
      description: p.description ?? '',
      instruction: p.instruction ?? null,
      areaDesc: p.areaDesc ?? '',
      severity: p.severity ?? 'Unknown',
      urgency: p.urgency ?? 'Unknown',
      certainty: p.certainty ?? 'Unknown',
      effective: p.effective ?? null,
      expires: p.expires ?? null,
      senderName: p.senderName ?? '',
      geometry,
      rings,
      bbox: stats.bbox,
      centroid: stats.centroid,
      area: stats.area,
    };
  });
}
